import { useUserData } from '@conteg/auth';
import { Alert, Box, Flex, Heading, IconButton, Table, Text } from '@conteg/ui';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DebouncedSearchInput } from 'components/debounced-search-input/debounced-search-input';
import { FranchiseReportsLink } from 'components/franchise-reports-link/franchise-reports-link';
import { Link, TableWrapper, Td } from 'components/styled';
import { CUSTOM_AUTH_TOKEN } from 'config';
import { useCanViewFranchises } from 'hooks/use-can-view-franchises';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useFranchiseCompaniesQuery,
  useProjectsQuery,
} from 'types/generated/graphql';
import { getReportingWebOwnerUrl } from 'utils/reporting-web/reporting-web';
import { substituteRouteParams } from 'utils/routing/helpers';
import { RouteUrls } from 'utils/routing/router';

import { deriveFranchises, Franchise } from './derive-franchises';

type FranchiseRow = Franchise & {
  usersCount?: number;
  reportsUrl?: string;
};

const columnHelper = createColumnHelper<FranchiseRow>();

const matchesSearch = (row: FranchiseRow, searchTerm: string) => {
  const term = searchTerm.trim().toLocaleLowerCase('cs');

  if (!term) {
    return true;
  }

  return [row.ownerCompanyName, ...row.branches.map((b) => b.name)].some(
    (value) => value.toLocaleLowerCase('cs').includes(term)
  );
};

const getCompanyDetailUrl = (subjectId: string) =>
  substituteRouteParams(RouteUrls.CompanyDetail, { subjectId });

const FranchisesList = ({
  providerSubjectId,
}: {
  providerSubjectId: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: projectsData,
    isLoading,
    error,
  } = useProjectsQuery({ subjectIds: [providerSubjectId] });

  const franchises = useMemo(
    () => deriveFranchises(projectsData?.projects ?? [], providerSubjectId),
    [projectsData?.projects, providerSubjectId]
  );

  const ownerSubjectIds = useMemo(
    () => franchises.map((franchise) => franchise.ownerSubjectId),
    [franchises]
  );

  // An empty subjectIds filter would return every company — never send it.
  // company-api requires paging (pageIndex is 0-based); one page sized to the
  // number of owners returns all of them, so hasNextPage is always false.
  const { data: companiesData } = useFranchiseCompaniesQuery(
    {
      subjectIds: ownerSubjectIds,
      pageIndex: 0,
      pageSize: ownerSubjectIds.length,
    },
    { enabled: ownerSubjectIds.length > 0 }
  );

  const rows = useMemo<FranchiseRow[]>(() => {
    const usersCountBySubjectId = new Map(
      (companiesData?.companies.items ?? []).map((company) => [
        company.subjectId,
        company.teamsMembersCount,
      ])
    );

    return franchises
      .map((franchise) => ({
        ...franchise,
        usersCount: usersCountBySubjectId.get(franchise.ownerSubjectId),
        reportsUrl: getReportingWebOwnerUrl(franchise.ownerSubjectId),
      }))
      .filter((row) => matchesSearch(row, searchTerm));
  }, [companiesData?.companies.items, franchises, searchTerm]);

  const showReportsColumn = rows.some((row) => !!row.reportsUrl);

  const navigateToCompanyDetail = useCallback(
    (subjectId: string) => navigate(getCompanyDetailUrl(subjectId)),
    [navigate]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.ownerCompanyName, {
        id: 'name',
        cell: (info) => (
          <Link
            to={getCompanyDetailUrl(info.row.original.ownerSubjectId)}
            data-test="franchiseName"
          >
            {info.getValue()}
          </Link>
        ),
        header: () => <Td>{t('Franchises.Table.Column.Name')}</Td>,
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.branches.length, {
        id: 'branchesCount',
        cell: (info) => (
          <span data-test="franchiseBranchesCount">{info.getValue()}</span>
        ),
        header: () => <Td>{t('Franchises.Table.Column.BranchesCount')}</Td>,
        enableSorting: false,
      }),
      columnHelper.accessor(
        (row) => row.branches.map((branch) => branch.name).join(', '),
        {
          id: 'branches',
          cell: (info) => info.getValue(),
          header: () => <Td>{t('Franchises.Table.Column.Branches')}</Td>,
          enableSorting: false,
        }
      ),
      columnHelper.accessor((row) => row.usersCount, {
        id: 'usersCount',
        cell: (info) => info.getValue() ?? '–',
        header: () => <Td>{t('Franchises.Table.Column.UsersCount')}</Td>,
        enableSorting: false,
      }),
      ...(showReportsColumn
        ? [
            columnHelper.accessor((row) => row.reportsUrl, {
              id: 'reports',
              cell: (info) => {
                const url = info.getValue();
                return url ? (
                  <FranchiseReportsLink
                    href={url}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t('Franchises.Table.ReportsLink')}
                  </FranchiseReportsLink>
                ) : null;
              },
              header: () => <Td>{t('Franchises.Table.Column.Reports')}</Td>,
              enableSorting: false,
            }),
          ]
        : []),
      columnHelper.accessor((row) => row.ownerSubjectId, {
        id: 'detail',
        enableSorting: false,
        cell: (info) => (
          <Flex justifyContent="flex-end">
            <IconButton
              testId="franchiseDetail"
              name="chevronRight"
              onClick={() => navigateToCompanyDetail(info.getValue())}
            />
          </Flex>
        ),
        header: () => <></>,
      }),
    ],
    [navigateToCompanyDetail, showReportsColumn, t]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  return (
    <TableWrapper flexDirection="column">
      <Flex justifyContent="space-between" mb="10rem">
        <Box width="76rem">
          <DebouncedSearchInput
            label={t('Franchises.Table.Search')}
            onChange={setSearchTerm}
            testId="franchisesTableSearch"
          />
        </Box>
      </Flex>
      <Table
        table={table}
        isLoading={isLoading}
        isEmpty={!isLoading && !error && rows.length === 0}
        onRowClick={(row) => navigateToCompanyDetail(row.ownerSubjectId)}
        errorInfo={{
          error,
          shortDescription: {
            title: t('Table.ShortDescriptionErrorTitle'),
            description: t('Franchises.Error.Title'),
          },
          extendedDescription: {
            title: t('Franchises.Error.Title'),
          },
        }}
        emptyState={{
          title: t('Franchises.Table.EmptyState.Title'),
          description: t('Franchises.Table.EmptyState.Description'),
        }}
      />
    </TableWrapper>
  );
};

const Franchises = () => {
  const { t } = useTranslation();
  const userData = useUserData(CUSTOM_AUTH_TOKEN);
  const canViewFranchises = useCanViewFranchises();

  return (
    <Flex flexDirection="column" gap="4rem">
      <Heading title={t('Franchises.Header')} variant="h1" />
      {canViewFranchises && userData?.subjectId ? (
        <>
          <Text content={t('Franchises.Description')} />
          <FranchisesList providerSubjectId={userData.subjectId} />
        </>
      ) : (
        <Alert type="error" message={t('Franchises.Error.Forbidden')} />
      )}
    </Flex>
  );
};

export default Franchises;
